import {
    OrderType,
    UpsellApi,
    UpsellConditionApi,
    UpsellConditionOperator,
    UpsellConditionType
} from "../../../Api/types";
import HoursHelper from "../Availability/HoursHelper";
import DateService from "../../../Common/Date/DateService";
import AvailableHelper from "../Availability/AvailableHelper";

class UpsellHelper {
    static getValidUpsells(
        upsells: UpsellApi[],
        cart: Record<string, any>,
        orderType: OrderType,
        items: Record<string, any>[],
        itemGroups: Record<string, any>[],
        availabilities: Record<string, any>[]
    ) {
        return upsells.filter(upsell => upsell.status === "ENABLED" && UpsellHelper.isUpsellValid(upsell, cart, orderType, items, itemGroups, availabilities));
    }

    static isUpsellValid(
        upsell: UpsellApi,
        cart: Record<string, any>,
        orderType: OrderType,
        items: Record<string, any>[],
        itemGroups: Record<string, any>[],
        availabilities: Record<string, any>[]
    ) {
        if (upsell.expressions.length === 0) return true;

        return upsell.expressions.some(expression => {
            return expression.conditions.every(condition => UpsellHelper.validateUpsellCondition(condition, cart, orderType, items, itemGroups, availabilities));
        });
    }

    static validateUpsellCondition(
        condition: UpsellConditionApi,
        cart: Record<string, any>,
        orderType: OrderType,
        items: Record<string, any>[],
        itemGroups: Record<string, any>[],
        availabilities: Record<string, any>[]
    ) {
        switch (condition.type) {
            case UpsellConditionType.ORDER_TOTAL_PRICE:
                return UpsellHelper.validateOrderTotalPriceCondition(condition, cart);
            case UpsellConditionType.ORDER_TYPE:
                return UpsellHelper.validateOrderTypeCondition(condition, orderType);
            case UpsellConditionType.ORDER_ITEM_COUNT:
                return UpsellHelper.validateOrderItemCountCondition(condition, cart, items, itemGroups);
            case UpsellConditionType.AVAILABILITY:
                return UpsellHelper.validateAvailabilityCondition(condition, availabilities);
            case UpsellConditionType.ORDER_SOURCE:
                return UpsellHelper.validateOrderSourceCondition(condition);
            default:
                return false;
        }
    }

    private static validateOrderTotalPriceCondition(condition: UpsellConditionApi, cart: Record<string, any>) {
        const orderPrice = cart.sub_total?.amount ?? 0;
        const value = Number(condition.value);

        switch (condition.operator) {
            case UpsellConditionOperator.EQUALS:
                return orderPrice === value;
            case UpsellConditionOperator.NOT_EQUALS:
                return orderPrice !== value;
            case UpsellConditionOperator.GREATER_THAN:
                return orderPrice > value;
            case UpsellConditionOperator.GREATER_THAN_OR_EQUAL_TO:
                return orderPrice >= value;
            case UpsellConditionOperator.LESS_THAN:
                return orderPrice < value;
            case UpsellConditionOperator.LESS_THAN_OR_EQUAL_TO:
                return orderPrice <= value;
        }
    }

    private static validateOrderTypeCondition(condition: UpsellConditionApi, currentOrderType: OrderType) {
        const value = condition.value.split(",");

        switch (condition.operator) {
            case UpsellConditionOperator.EQUALS:
                return value.includes(currentOrderType);
            case UpsellConditionOperator.NOT_EQUALS:
                return !value.includes(currentOrderType);
        }
    }

    private static validateOrderSourceCondition(condition: UpsellConditionApi) {
        const value = condition.value.split(",");
        const currentSource = "GoOrder";

        switch (condition.operator) {
            case UpsellConditionOperator.EQUALS:
                return value.includes(currentSource);
            case UpsellConditionOperator.NOT_EQUALS:
                return !value.includes(currentSource);
        }
    }

    private static validateAvailabilityCondition(condition: UpsellConditionApi, availabilities: Record<string, any>[]) {
        const value = condition.value.split(",").map((element) => Number(element));
        const now = DateService.now();
        const validAvailabilities = availabilities.filter(availability => value.includes(availability.id));

        switch (condition.operator) {
            case UpsellConditionOperator.EQUALS:
                return validAvailabilities.some(availability => {
                    return HoursHelper.isOpen(availability.hours, now) && AvailableHelper.isAvailabile(availability, now);
                });
            default:
                return false;
        }
    }

    private static validateOrderItemCountCondition(
        condition: UpsellConditionApi,
        cart: Record<string, any>,
        items: Record<string, any>[],
        itemGroups: Record<string, any>[]
    ) {
        const value = Number(condition.value);
        const orderItems = cart.items;
        const count = orderItems.filter((orderItem: Record<string, any>) => {
            if (condition.item_id && condition.item_id !== orderItem.item_id) return false;

            if (condition.category_id) {
                const item = items.find(item => item.id === orderItem.item_id);

                if (!item || item.category_id !== condition.category_id) return false;
            }

            if (condition.item_group_id) {
                const itemGroup = itemGroups.find(itemGroup => itemGroup.id === condition.item_group_id);
                if(!itemGroup) return false;
                if(!itemGroup.items.some((itemGroupItem:any) => itemGroupItem.item_id === orderItem.item_id)) return false;
            }


            return true;
        }).reduce((sum: number, activeItem: Record<string, any>) => {
            return sum + activeItem.quantity;
        }, 0);

        switch (condition.operator) {
            case UpsellConditionOperator.EQUALS:
                return count === value;
            case UpsellConditionOperator.NOT_EQUALS:
                return count !== value;
            case UpsellConditionOperator.GREATER_THAN:
                return count > value;
            case UpsellConditionOperator.GREATER_THAN_OR_EQUAL_TO:
                return count >= value;
            case UpsellConditionOperator.LESS_THAN:
                return count < value;
            case UpsellConditionOperator.LESS_THAN_OR_EQUAL_TO:
                return count <= value;
        }
    }
}

export default UpsellHelper;